'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useOrganization } from '@clerk/clerk-react';
import { trpc } from '@/lib/api/trpc';
import { Users, Mail, Crown, UserX, MoreHorizontal, UserPlus, Send, Edit, ShieldAlert } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { usePerm, PERMISSIONS, ROLE_INFO, ROLES } from '@/hooks/use-perm';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useToast } from '@/components/ui/use-toast';
import { format } from 'date-fns';

function InviteMemberDialog() {
  const { t } = useTranslation('settings');
  const { organization, invitations } = useOrganization();
  const { toast } = useToast();
  const [open, setOpen] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [role, setRole] = React.useState('org:member');
  const [isInviting, setIsInviting] = React.useState(false);
  // Server-side invitation: the backend sets the `redirectUrl` so the
  // email link lands on the primary's /accept-invitation page. Clerk's
  // frontend `organization.inviteMember()` cannot set it and links to
  // `accounts.<partner-domain>/sign-up`, a host that does not exist for
  // partner satellites (Kashu, RetryHub… — see TAG-2678).
  const inviteOwn = trpc.iamOrgs.membersInviteOwn.useMutation();

  const handleInvite = async () => {
    if (!email.trim() || !organization) return;

    try {
      setIsInviting(true);

      await inviteOwn.mutateAsync({
        email: email.trim(),
        role: role as 'org:admin' | 'org:member',
      });

      toast({
        title: t('organization.members.toast.invitationSent'),
        description: t('organization.members.toast.invitationSentTo', { email }),
      });

      // Refresh the invitations data
      await invitations?.revalidate?.();

      setEmail('');
      setRole('org:member');
      setOpen(false);
    } catch (error: any) {
      toast({
        title: t('organization.members.toast.failedToSend'),
        description: error.message || t('organization.members.toast.invitationError'),
        variant: 'destructive',
      });
    } finally {
      setIsInviting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="mr-2 h-4 w-4" />
          {t('organization.members.inviteMember')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('organization.members.inviteNewMember')}</DialogTitle>
          <DialogDescription>
            {t('organization.members.inviteDescription')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">{t('organization.members.emailAddress')}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t('organization.members.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="role">{t('organization.members.role')}</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="org:member">{t('organization.membership.member')}</SelectItem>
                <SelectItem value="org:admin">{t('organization.membership.admin')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t('organization.members.cancel')}
          </Button>
          <Button onClick={handleInvite} disabled={!email.trim() || isInviting}>
            {isInviting ? (
              <>
                <Send className="mr-2 h-4 w-4 animate-pulse" />
                {t('organization.members.sending')}
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                {t('organization.members.sendInvitation')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditMemberDialog({ member, onClose }: { member: any; onClose: () => void }) {
  const { t } = useTranslation('settings');
  const { organization, memberships } = useOrganization();
  const { toast } = useToast();
  const [open, setOpen] = React.useState(true);
  const [role, setRole] = React.useState(member.role);
  const [isUpdating, setIsUpdating] = React.useState(false);

  const handleUpdateRole = async () => {
    if (!organization || role === member.role) {
      onClose();
      return;
    }

    try {
      setIsUpdating(true);

      // Use Clerk's organization update member API
      await organization.updateMember({
        userId: member.publicUserData?.userId,
        role: role as any,
      });

      toast({
        title: t('organization.members.toast.roleUpdated'),
        description: t('organization.members.toast.roleUpdatedSuccess'),
      });

      // Refresh the memberships data
      await memberships?.revalidate?.();

      onClose();
    } catch (error: any) {
      toast({
        title: t('organization.members.toast.failedToUpdate'),
        description: error.message || t('organization.members.toast.invitationError'),
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('organization.members.editMemberRole')}</DialogTitle>
          <DialogDescription>
            {t('organization.members.updateRoleFor', { name: `${member.publicUserData?.firstName} ${member.publicUserData?.lastName}` })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t('organization.members.currentRole')}</Label>
            <Badge variant={member.role === 'org:admin' ? 'destructive' : 'secondary'}>
              {member.role === 'org:admin' ? t('organization.membership.admin') : t('organization.membership.member')}
            </Badge>
          </div>
          <div className="space-y-2">
            <Label htmlFor="role">{t('organization.members.newRole')}</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="org:member">{t('organization.membership.member')}</SelectItem>
                <SelectItem value="org:admin">{t('organization.membership.admin')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {role === 'org:admin' && (
            <div className="rounded-md bg-yellow-50 p-3 dark:bg-yellow-950/20">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>{t('organization.members.warning')}</strong> {t('organization.members.adminWarning')}
              </p>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('organization.members.cancel')}
          </Button>
          <Button onClick={handleUpdateRole} disabled={role === member.role || isUpdating}>
            {isUpdating ? (
              <>
                <Edit className="mr-2 h-4 w-4 animate-pulse" />
                {t('organization.members.updating')}
              </>
            ) : (
              <>
                <Edit className="mr-2 h-4 w-4" />
                {t('organization.members.updateRole')}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OrganizationMembers() {
  const { t } = useTranslation('settings');
  const { organization, membership, memberships, invitations, isLoaded } = useOrganization({
    memberships: { pageSize: 100 },
    invitations: { pageSize: 100 },
  });
  const { toast } = useToast();
  const { can } = usePerm();
  const [editingMember, setEditingMember] = React.useState<any>(null);

  const handleRemoveMember = async (userId: string, memberName: string) => {
    if (!organization) return;

    try {
      await organization.removeMember(userId);
      toast({
        title: t('organization.members.toast.memberRemoved'),
        description: t('organization.members.toast.memberRemovedSuccess'),
      });

      // Refresh the memberships data
      await memberships?.revalidate?.();
    } catch (error: any) {
      toast({
        title: t('organization.members.toast.failedToRemove'),
        description: error.message || t('organization.members.toast.invitationError'),
        variant: 'destructive',
      });
    }
  };

  const handleRevokeInvitation = async (invitationId: string, email: string) => {
    if (!organization) return;

    try {
      // Use the correct Clerk method for revoking invitations
      const invitation = invitations?.data?.find((inv) => inv.id === invitationId);
      if (invitation) {
        await invitation.revoke();
      }
      toast({
        title: t('organization.members.toast.invitationRevoked'),
        description: t('organization.members.toast.invitationRevokedSuccess'),
      });

      // Refresh the invitations data
      await invitations?.revalidate?.();
    } catch (error: any) {
      toast({
        title: t('organization.members.toast.failedToRevoke'),
        description: error.message || t('organization.members.toast.invitationError'),
        variant: 'destructive',
      });
    }
  };

  if (!isLoaded) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {t('organization.members.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="bg-muted h-10 w-10 animate-pulse rounded-full" />
                <div className="flex-1 space-y-2">
                  <div className="bg-muted h-4 w-32 animate-pulse rounded" />
                  <div className="bg-muted h-3 w-48 animate-pulse rounded" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!organization) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Users className="text-muted-foreground mx-auto h-12 w-12" />
          <h3 className="mt-4 text-lg font-semibold">{t('organization.overview.noOrgSelected')}</h3>
          <p className="text-muted-foreground mt-2 text-sm">{t('organization.overview.pleaseSelectOrg')}</p>
        </CardContent>
      </Card>
    );
  }

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'org:admin':
        return 'destructive';
      case 'org:member':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getRoleName = (role: string) => {
    if (role === ROLES.ADMIN) return ROLE_INFO[ROLES.ADMIN].name;
    if (role === ROLES.MEMBER) return ROLE_INFO[ROLES.MEMBER].name;
    return role.replace('org:', '');
  };

  const getRoleDescription = (role: string) => {
    if (role === ROLES.ADMIN) return ROLE_INFO[ROLES.ADMIN].description;
    if (role === ROLES.MEMBER) return ROLE_INFO[ROLES.MEMBER].description;
    return '';
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || '?';
  };

  const canManageMembers = can(PERMISSIONS.TEAM_MANAGE);

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('organization.members.title')}
              </CardTitle>
              <p className="text-muted-foreground mt-1 text-sm">
                {t('organization.members.description')}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline">{memberships?.count || 0} {t('organization.membership.totalMembers')}</Badge>
              {invitations?.data && invitations.data.length > 0 && (
                <Badge variant="yellow" className="text-xs">
                  {invitations.data.length} {t('organization.members.pending')}
                </Badge>
              )}
              {canManageMembers && <InviteMemberDialog />}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {/* Pending Invitations */}
            {invitations?.data && invitations.data.length > 0 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold">{t('organization.members.pendingInvitations')}</h3>
                  <Badge variant="yellow" className="text-xs">
                    {invitations.data.length}
                  </Badge>
                </div>
                <div className="space-y-3">
                  {invitations.data.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="flex flex-col items-start gap-3 rounded-lg border border-dashed border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-950/20 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-yellow-100 dark:bg-yellow-900/30">
                          <Mail className="h-4 w-4 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div>
                          <p className="font-medium">{invitation.emailAddress}</p>
                          <div className="text-muted-foreground flex items-center gap-2 text-sm">
                            <Badge variant="outline" className="text-xs">
                              {invitation.role === 'org:admin' ? t('organization.membership.admin') : t('organization.membership.member')}
                            </Badge>
                            <span>{t('organization.members.invitedAt')} {format(new Date(invitation.createdAt), 'MMM dd, yyyy')}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                        <CopyableResourceBadge
                          name={t('organization.members.invitationId')}
                          value={invitation.id}
                          className="font-mono text-xs"
                        />
                        {canManageMembers && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRevokeInvitation(invitation.id, invitation.emailAddress)}
                          >
                            <UserX className="mr-2 h-4 w-4" />
                            {t('organization.members.revoke')}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Current Members */}
            <div className="space-y-4">
              {memberships?.data && memberships.data.length > 0 && (
                <h3 className="text-sm font-semibold">{t('organization.members.currentMembers')}</h3>
              )}
              {memberships?.data?.map((member) => (
                <div key={member.id} className="flex flex-col items-start gap-3 rounded-lg border p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex min-w-0 flex-1 items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={member.publicUserData?.imageUrl} />
                      <AvatarFallback>
                        {getInitials(
                          member.publicUserData?.firstName || undefined,
                          member.publicUserData?.lastName || undefined,
                        )}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium">
                          {member.publicUserData?.firstName} {member.publicUserData?.lastName}
                        </p>
                        {member.role === 'org:admin' && <Crown className="h-4 w-4 text-yellow-500" />}
                      </div>
                      <div className="text-muted-foreground flex items-center gap-2 text-sm">
                        <Mail className="h-3 w-3" />
                        {member.publicUserData?.identifier}
                      </div>
                      <div className="mt-1 flex items-center gap-2">
                        <Badge variant={getRoleColor(member.role)} className="text-xs" title={getRoleDescription(member.role)}>
                          {getRoleName(member.role)}
                        </Badge>
                        <span className="text-muted-foreground text-xs">
                          {t('organization.members.joined')} {format(new Date(member.createdAt), 'MMM dd, yyyy')}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex w-full items-center gap-2 sm:w-auto sm:justify-end">
                    <CopyableResourceBadge
                      name={t('organization.members.userId')}
                      value={member.publicUserData?.userId || ''}
                      className="font-mono text-xs"
                    />
                    {canManageMembers &&
                      member.publicUserData?.userId !== membership?.publicUserData?.userId && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setEditingMember(member)}>
                              <Edit className="mr-2 h-4 w-4" />
                              {t('organization.members.updateRole')}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() =>
                                handleRemoveMember(
                                  member.publicUserData?.userId || '',
                                  `${member.publicUserData?.firstName} ${member.publicUserData?.lastName}`,
                                )
                              }
                            >
                              <UserX className="mr-2 h-4 w-4" />
                              {t('organization.members.removeMember')}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                  </div>
                </div>
              ))}

              {(!memberships?.data || memberships.data.length === 0) && (
                <div className="py-8 text-center">
                  <Users className="text-muted-foreground mx-auto h-12 w-12" />
                  <h3 className="mt-4 text-lg font-semibold">{t('organization.members.noMembers')}</h3>
                  <p className="text-muted-foreground mt-2 text-sm">{t('organization.overview.pleaseSelectOrg')}</p>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Member Dialog */}
      {editingMember && <EditMemberDialog member={editingMember} onClose={() => setEditingMember(null)} />}
    </>
  );
}
